// « Accepté par le client » : information à part, saisie après l'émission. La révision elle-même reste figée.
import { useState } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { BadgeCheck, Undo2 } from 'lucide-react';
import { api, messageErreur } from '../../lib/api.ts';
import { date, dateHeure } from '../../lib/format.ts';
import { dateDuJourParis } from '@shared/dates.ts';
import { Alerte, Bouton, Champ, ChampChoix, ChampTexte, Dialogue } from '../../components/ui.tsx';

export interface Acceptation {
  id: string; accepted_on: string; note: string | null; enregistree_par: string | null; enregistree_le: string;
  piece: { version_id: string; titre: string } | null;
  remplacee: { le: string; rev_no: number | null } | null;
}

interface Piece { id: string; title: string; versions: { id: string; version_label: string; file_name: string }[] }

/** Bandeau : « Acceptée par le client le … » (ou remplacée par une révision plus récente). */
export function BandeauAcceptation({ a, peutAnnuler, annuler }: { a: Acceptation; peutAnnuler: boolean; annuler: () => void }) {
  if (a.remplacee) {
    return (
      <Alerte ton="info" titre={`Acceptée par le client le ${date(a.accepted_on)} — remplacée`}>
        Une révision plus récente{a.remplacee.rev_no ? ` (révision ${a.remplacee.rev_no})` : ''} a été acceptée le {dateHeure(a.remplacee.le)}.
      </Alerte>
    );
  }
  return (
    <Alerte ton="succes" titre={`Acceptée par le client le ${date(a.accepted_on)}`}
      action={peutAnnuler ? <Bouton compact variante="fantome" icone={<Undo2 aria-hidden />} onClick={annuler}>Annuler l’acceptation</Bouton> : undefined}>
      <span>Saisie{a.enregistree_par ? ` par ${a.enregistree_par}` : ''} le {dateHeure(a.enregistree_le)}. </span>
      {a.piece && <span>Pièce : <Link to={`/documentation/lecteur/${a.piece.version_id}`}>{a.piece.titre}</Link>. </span>}
      {a.note && <span>« {a.note} » </span>}
      <span>La révision reste figée telle qu’émise.</span>
    </Alerte>
  );
}

export function DialogueAcceptation({ ouvert, fermer, dossierId, emiseLe, accepter }: {
  ouvert: boolean; fermer: () => void; dossierId: string; emiseLe: string | null;
  accepter: (corps: { acceptedOn: string; proofVersionId: string | null; note: string | null }) => Promise<unknown>;
}) {
  const aujourdhui = dateDuJourParis();
  const [jour, setJour] = useState(aujourdhui);
  const [piece, setPiece] = useState('');
  const [note, setNote] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const pieces = useQuery({ queryKey: ['dossier-pieces', dossierId], enabled: ouvert,
    queryFn: () => api.get<{ pieces: Piece[] }>(`/api/dossiers/${dossierId}/pieces`) });
  const options = (pieces.data?.pieces ?? []).flatMap((p) => p.versions.slice(0, 1).map((v) => ({ valeur: v.id, libelle: `${p.title} — ${v.file_name}` })));
  const min = emiseLe ? dateDuJourParis(new Date(emiseLe)) : undefined;
  const valider = async () => {
    setErreur(null);
    if (!jour) { setErreur('Indiquez la date d’acceptation.'); return; }
    setEnvoi(true);
    try { await accepter({ acceptedOn: jour, proofVersionId: piece || null, note: note.trim() || null }); fermer(); }
    catch (e) { setErreur(messageErreur(e)); } finally { setEnvoi(false); }
  };
  return (
    <Dialogue titre="Marquer accepté par le client" ouvert={ouvert} fermer={fermer}
      pied={<><Bouton onClick={fermer}>Annuler</Bouton><Bouton variante="principal" icone={<BadgeCheck aria-hidden />} chargement={envoi} onClick={valider}>Enregistrer l’acceptation</Bouton></>}>
      <div className="pile">
        <p className="petit secondaire">« Validée » reste la validation interne avant émission. L’acceptation du client est une information distincte : le devis émis ne change pas.</p>
        {erreur && <Alerte ton="danger">{erreur}</Alerte>}
        <Champ libelle="Date d’acceptation" type="date" value={jour} max={aujourdhui} min={min} onChange={(e) => setJour(e.target.value)} />
        <ChampChoix libelle="Bon de commande ou devis signé (facultatif)" value={piece} onChange={(e) => setPiece(e.target.value)} vide="Aucune pièce"
          options={options} aide={pieces.isLoading ? 'Chargement des pièces du dossier…' : 'Déposez d’abord la pièce dans le dossier (onglet Pièces) pour la choisir ici.'} />
        <ChampTexte libelle="Note (facultative)" rows={2} maxLength={2000} value={note} onChange={(e) => setNote(e.target.value)} />
      </div>
    </Dialogue>
  );
}

export function DialogueAnnulationAcceptation({ ouvert, fermer, annuler }: { ouvert: boolean; fermer: () => void; annuler: (motif: string) => Promise<unknown> }) {
  const [motif, setMotif] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const valider = async () => {
    if (motif.trim().length < 5) { setErreur('Indiquez le motif de l’annulation (5 caractères au moins).'); return; }
    setEnvoi(true); setErreur(null);
    try { await annuler(motif.trim()); fermer(); } catch (e) { setErreur(messageErreur(e)); } finally { setEnvoi(false); }
  };
  return (
    <Dialogue titre="Annuler l’acceptation du client" ouvert={ouvert} fermer={fermer}
      pied={<><Bouton onClick={fermer}>Retour</Bouton><Bouton variante="danger" chargement={envoi} onClick={valider}>Annuler l’acceptation</Bouton></>}>
      <div className="pile">
        <p className="petit secondaire">L’annulation est conservée avec son motif dans le journal ; rien n’est effacé.</p>
        {erreur && <Alerte ton="danger">{erreur}</Alerte>}
        <ChampTexte libelle="Motif" rows={3} maxLength={2000} value={motif} onChange={(e) => setMotif(e.target.value)} />
      </div>
    </Dialogue>
  );
}
